import { resolveSaasMediaUrl, type CanvasVoiceTemplateDto, type GenerateCanvasVoiceTemplatePayload } from '../../../api'
import { buildProviderOptionNodeDefaultFields } from './capabilityProviderSelection'

export type VoiceTemplateSourceType = 'AI_GENERATED' | 'REFERENCE' | 'CLONED'

export const VOICE_TEMPLATE_SOURCE_TYPES: VoiceTemplateSourceType[] = ['AI_GENERATED', 'REFERENCE', 'CLONED']

/** Capability for Provider Option footer — Admin-seeded MiniMax voice template. */
export const VOICE_TEMPLATE_CAPABILITY = 'audio.voice_template'

/** UI-local Skill (mirrors Generate Audio) — Skill ≠ Provider. */
export const VOICE_TEMPLATE_SKILL_KEY = 'voice_template_generation'
export const VOICE_TEMPLATE_SKILL_VERSION = 'v1'

/** Product semantic params mapped by Skill into voice_brief — not raw MiniMax API fields. */
export type VoiceTemplateLanguage = 'zh-CN' | 'en-US'
export type VoiceTemplateGender = 'any' | 'male' | 'female'
export type VoiceTemplateStyle = 'natural' | 'calm' | 'gentle' | 'lively' | 'professional' | 'narrative'
export type VoiceTemplateScene = 'general' | 'ad' | 'narration' | 'audiobook' | 'customer_service' | 'character'

export type VoiceTemplateGenerationHistoryEntry = {
  version: number
  templateId: string | null
  previewAudioAssetId: string | null
  templateName: string
  sourceType: VoiceTemplateSourceType
  voiceDescription: string
  previewText: string
  language: VoiceTemplateLanguage
  gender: VoiceTemplateGender
  style: VoiceTemplateStyle
  scene: VoiceTemplateScene
  referenceAssetId: string | null
  providerOptionId: string | null
  skillKey: string | null
  skillVersion: string | null
  completedAt: string
}

export function appendVoiceTemplateGenerationHistoryEntry(
  history: VoiceTemplateGenerationHistoryEntry[],
  entry: VoiceTemplateGenerationHistoryEntry,
  maxEntries = 20,
): VoiceTemplateGenerationHistoryEntry[] {
  return [...history, entry].slice(-maxEntries)
}

export function buildGenerateVoiceTemplateNodeDefaultFields(): Record<string, unknown> {
  return {
    sourceType: 'AI_GENERATED' as VoiceTemplateSourceType,
    voiceDescription: '',
    previewText: '',
    templateName: '',
    language: 'zh-CN' as VoiceTemplateLanguage,
    gender: 'any' as VoiceTemplateGender,
    style: 'natural' as VoiceTemplateStyle,
    scene: 'general' as VoiceTemplateScene,
    referenceAssetId: null,
    referenceFilename: null,
    referenceDurationMs: null,
    referenceCaptureMethod: null,
    voiceRightsConfirmed: false,
    status: 'READY',
    templateId: null,
    previewAudioAssetId: null,
    previousPreviewAudioAssetId: null,
    providerVoiceId: null,
    provider: null,
    errorMessage: null,
    needsRegeneration: false,
    candidateForGenerateAudioNodeId: null,
    generationHistory: [],
    skillSelectionSource: 'AUTO',
    skillKey: null,
    skillVersion: null,
    ...buildProviderOptionNodeDefaultFields(),
  }
}

/** VistoeraTASK-00187 — canonical mapper from the backend's
 * `CanvasVoiceTemplateDto` (the real, persisted generation result, always
 * resolvable by `canvas_node_id` alone — see `_get_owned_template`) into
 * this node's local `fields` shape. Used by BOTH the initial-load hydrate
 * effect AND the "self-heal" rehydrate-by-node-id path in
 * `GenerateVoiceTemplateNode.tsx` (a real backend SUCCEEDED result must
 * always be able to repopulate this node's fields, regardless of whether
 * this component's own local `fields.templateId` pointer is present or
 * went stale — see that file's hydrate effect for the forensic finding).
 *
 * A `GENERATING` status observed on (re)hydrate means the original
 * request was interrupted (e.g. tab closed/refreshed mid-request, or a
 * client-side timeout while the backend kept running) — Canvas Voice
 * Template generation is synchronous end-to-end, there is no background
 * job to keep polling, so this is surfaced as an actionable failure
 * rather than a permanently-stuck "generating" spinner. */
export function mapVoiceTemplateDtoToFields(
  dto: CanvasVoiceTemplateDto,
  opts?: {
    interruptedGenerationMessage?: string
    // VistoeraTASK-00189 — canonical `SaasApiError`-compatible shape
    // (`code`/`message` are `string | undefined`, matching
    // `Pick<SaasApiError, 'code' | 'status' | 'message'>`, which
    // `formatCanvasVoiceTemplateError` requires). The backend DTO's own
    // `error_code`/`error_message` fields are `string | null` (a
    // JSON/Python `None`, not a thrown-exception's absent property) —
    // these are two legitimately different canonical shapes for
    // "absent", so the `null -> undefined` normalization happens once,
    // right at this boundary, below (not by widening `SaasApiError`
    // itself to allow `null`, which would be the wrong contract for
    // every other real thrown-exception call site).
    formatErrorMessage?: (err: { code?: string; message: string }) => string
  },
): Record<string, unknown> {
  const ref = dto.reference_audio_assets?.[0]
  let status = 'READY'
  if (dto.status === 'SUCCEEDED') status = 'COMPLETED'
  else if (dto.status === 'FAILED') status = 'FAILED'
  else if (dto.status === 'GENERATING') status = 'FAILED'

  const rawErrorMessage = dto.error_message
  const errorMessage =
    dto.status === 'GENERATING'
      ? (opts?.interruptedGenerationMessage ?? rawErrorMessage)
      : rawErrorMessage
        ? (opts?.formatErrorMessage
            ? opts.formatErrorMessage({ code: dto.error_code ?? undefined, message: rawErrorMessage })
            : rawErrorMessage)
        : rawErrorMessage

  return {
    sourceType: dto.source_type,
    voiceDescription: dto.voice_brief || '',
    previewText: dto.preview_text || '',
    templateName: dto.name || '',
    referenceAssetId: dto.reference_audio_asset_ids?.[0] || null,
    referenceFilename: ref?.filename || null,
    referenceDurationMs: ref?.duration_ms ?? null,
    voiceRightsConfirmed: dto.voice_rights_confirmed,
    status,
    templateId: dto.template_id,
    previewAudioAssetId: dto.preview_audio_asset_id,
    providerVoiceId: dto.provider_voice_id,
    provider: dto.provider,
    errorMessage,
    needsRegeneration: false,
  }
}

/** Skill maps product semantics into provider voice_brief without inventing MiniMax params. */
export function composeVoiceTemplateBrief(args: {
  sourceType: VoiceTemplateSourceType
  voiceDescription: string
  language: VoiceTemplateLanguage
  gender: VoiceTemplateGender
  style: VoiceTemplateStyle
  scene: VoiceTemplateScene
}): string {
  const base = args.voiceDescription.trim()
  const hints: string[] = []
  if (args.language === 'en-US') hints.push('Language: English')
  else hints.push('语言：中文（普通话）')
  if (args.gender === 'male') hints.push(args.language === 'en-US' ? 'Gender: male' : '性别：男声')
  else if (args.gender === 'female') hints.push(args.language === 'en-US' ? 'Gender: female' : '性别：女声')
  const styleMap: Record<VoiceTemplateStyle, { zh: string; en: string }> = {
    natural: { zh: '自然', en: 'natural' },
    calm: { zh: '沉稳', en: 'calm' },
    gentle: { zh: '温柔', en: 'gentle' },
    lively: { zh: '活泼', en: 'lively' },
    professional: { zh: '专业', en: 'professional' },
    narrative: { zh: '叙事', en: 'narrative' },
  }
  const sceneMap: Record<VoiceTemplateScene, { zh: string; en: string }> = {
    general: { zh: '通用', en: 'general' },
    ad: { zh: '广告', en: 'advertising' },
    narration: { zh: '旁白', en: 'narration' },
    audiobook: { zh: '有声内容', en: 'audiobook' },
    customer_service: { zh: '客服', en: 'customer service' },
    character: { zh: '角色', en: 'character' },
  }
  const loc = args.language === 'en-US' ? 'en' : 'zh'
  hints.push(loc === 'en' ? `Style: ${styleMap[args.style].en}` : `风格：${styleMap[args.style].zh}`)
  hints.push(loc === 'en' ? `Scene: ${sceneMap[args.scene].en}` : `场景：${sceneMap[args.scene].zh}`)

  if (args.sourceType === 'AI_GENERATED') {
    return base ? `${base}\n\n(${hints.join('；')})` : hints.join('；')
  }
  // Clone / reference: optional brief only — do not inject AI synthesis gender/style/scene hints.
  return base
}

/**
 * Effective generation payload — only the active source mode contributes.
 * Hidden REFERENCE draft assets are excluded when source is AI_GENERATED.
 */
export function buildVoiceTemplateGeneratePayload(args: {
  sourceType: VoiceTemplateSourceType
  voiceDescription: string
  previewText: string
  templateName: string
  referenceAssetId: string | null
  voiceRightsConfirmed: boolean
  language: VoiceTemplateLanguage
  gender: VoiceTemplateGender
  style: VoiceTemplateStyle
  scene: VoiceTemplateScene
}): GenerateCanvasVoiceTemplatePayload {
  const effectiveRef =
    args.sourceType === 'AI_GENERATED' ? null : args.referenceAssetId ? [args.referenceAssetId] : null
  const brief = composeVoiceTemplateBrief({
    sourceType: args.sourceType,
    voiceDescription: args.voiceDescription,
    language: args.language,
    gender: args.gender,
    style: args.style,
    scene: args.scene,
  })
  return {
    source_type: args.sourceType,
    voice_brief: brief.trim() || null,
    preview_text: args.previewText.trim() || null,
    template_name: args.templateName.trim() || null,
    reference_audio_asset_ids: effectiveRef,
    voice_rights_confirmed: args.sourceType === 'CLONED' ? args.voiceRightsConfirmed || null : null,
  }
}

export function canvasVoiceTemplatePlaybackUrl(workspaceId: string, assetId: string): string {
  return resolveSaasMediaUrl(
    `/api/saas/workspaces/${workspaceId}/assets/${assetId}/variants/original`,
  ) as string
}

export const VOICE_TEMPLATE_INSPIRATION_ZH = [
  '成熟稳重的中文男声，语速适中，表达清晰有逻辑，适合科技产品讲解。',
  '温柔细腻的女声，语气温和有耐心，适合生活类内容与品牌旁白。',
  '年轻活泼的叙述者，节奏轻快自然，适合短视频口播与产品介绍。',
  '沉稳专业的播报声，咬字清楚，适合新闻摘要与企业宣传。',
]

export const VOICE_TEMPLATE_INSPIRATION_EN = [
  'A mature, steady male voice with clear pacing — ideal for tech product narration.',
  'A warm, gentle female voice with a patient tone — suited to lifestyle content.',
  'A lively young narrator with natural rhythm — good for short-form product intros.',
  'A calm professional announcer voice — clear diction for news-style summaries.',
]
